import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { Backdrop, Box, ClickAwayListener } from "@mui/material";
import { useQueryClient } from "react-query";

import CustomTooltip from "../../Components/Others/tooltip";

import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useModalContext } from "../../Contexts/ModalContext";

import { useAppEssentials, usePrograms } from "../../CustomHooks/Queries";
import { useUpdateProgramPreviewStatus } from "../../CustomHooks/Mutations";

import { getNavItems, isActiveModule } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import { PersonOutline, SupportChat } from "../../Assets/Icons";
import CloseButton from "../../Components/Buttons/close-button";

export default function SideBar() {
  const { pathname } = useLocation();

  const {
    appUtilityProps: { hideDealsRoutes, hideCashRoutes },
  } = useAppUtilityContext();

  const navItems = useMemo(() => getNavItems({ hideDealsRoutes, hideCashRoutes }), [hideDealsRoutes, hideCashRoutes]);

  const { show: showIntercom } = useIntercom();

  const queryClient = useQueryClient();
  const { modalVisibility } = useModalContext();

  const { data: appEssentialsData } = useAppEssentials();
  const { data: programs } = usePrograms({
    enabled: appEssentialsData?.propertyHasProgram ?? false,
  });

  const { mutate: UpdateProgramPreviewStatus } = useUpdateProgramPreviewStatus({
    onSuccess: () => {
      queryClient.refetchQueries(APIQueryKeys.appEssentials);
    },
  });

  const [isProgramsTooltipOpen, setIsProgramsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!!programs?.my_program && appEssentialsData?.previewProgramOnLogin && !modalVisibility) {
      setIsProgramsTooltipOpen(true);
    }
  }, [programs?.my_program, appEssentialsData?.previewProgramOnLogin, modalVisibility]);

  const handlePopperClose = () => {
    if (isProgramsTooltipOpen) {
      UpdateProgramPreviewStatus();
      setIsProgramsTooltipOpen(false);
    }
  };

  return (
    <aside className="sidebar">
      <div>
        <div className="sidebar-logo">
          <StakeLogo className="dark-with-accentuated-horn" />
        </div>
        <nav className="sidebar-nav" aria-label="Primary">
          {navItems.map(({ Icon, navItemName, navRoute }, idx) => {
            const isActive = isActiveModule(navRoute, pathname);
            return (
              <Link
                key={idx}
                className={`sidebar-item${isActive ? " active" : ""}`}
                to={navRoute}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon />
                {navItemName}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="sidebar-bottom">
        {isProgramsTooltipOpen && (
          <Backdrop
            open={isProgramsTooltipOpen}
            sx={{
              zIndex: (theme) => theme.zIndex.tooltip - 1,
              background: "var(--backdrop-bg)",
              backdropFilter: "var(--backdrop-filter)",
            }}
          />
        )}
        <CustomTooltip
          className="program-popper-tooltip"
          title={
            <ClickAwayListener onClickAway={handlePopperClose}>
              <>
                <div className="program-popper-logo-section">
                  <img
                    src={programs?.my_program?.image_src?.primary}
                    alt={programs?.my_program?.name}
                    className="program-logo"
                  />
                  <CloseButton onClick={handlePopperClose} />
                </div>
                <div className="program-popper-content">
                  <h4 className="program-title">{programs?.my_program?.name}</h4>
                  <p className="program-description">
                    View your {programs?.my_program?.name} benefits inside your profile.
                  </p>
                </div>
              </>
            </ClickAwayListener>
          }
          open={isProgramsTooltipOpen}
          placement="right-end"
        >
          <Box
            sx={{
              position: "relative",
              zIndex: isProgramsTooltipOpen ? (theme) => theme.zIndex.tooltip + 1 : "auto",
            }}
          >
            <Link
              className={`sidebar-item${isActiveModule("/profile", pathname) ? " active" : ""}`}
              to="/profile"
              onClick={handlePopperClose}
              aria-current={isActiveModule("/profile", pathname) ? "page" : undefined}
            >
              <PersonOutline />
              Profile
            </Link>
          </Box>
        </CustomTooltip>
        <button className="sidebar-item" onClick={showIntercom}>
          <SupportChat />
          Support
        </button>
      </div>
    </aside>
  );
}
